import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {NgbDate} from '@ng-bootstrap/ng-bootstrap';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {debounceTime} from 'rxjs';

import {TranslocoService} from '@ngneat/transloco';

import {
  CalendarViewType,
  EventFilter,
  LengthRange,
  Lookups,
  PublicSearchFilter,
  PublicSearchFilterQueryParams,
  Sport
} from '@model';

import {AbstractComponent, FilterGroup, FilterGroupType, FilterItem} from '@components';
import {getDateFromNgbDate, normalizedDate, parseIdListParam} from '@utility';
import {environment} from '@environment';
import {LocalStorageService} from "@services";


export type FilterKey = 'sports' | 'country' | 'length' | 'status';

@Component({
  selector: 'app-public-search-filter',
  templateUrl: './public-search-filter.component.html',
  styleUrl: './public-search-filter.component.scss',
  standalone: false
})
export class PublicSearchFilterComponent extends AbstractComponent implements OnInit {
  @Input() showDateFilter = true
  @Input() disablePastMonths = false;
  @Input() disableFutureMonths = false;
  @Input() maxDate: NgbDate | undefined;
  @Input() minDate: NgbDate | undefined;
  @Input() showLayoutButton = true;
  @Input() showSearchBar = true;

  @Input() countries: Lookups[] = [];
  @Input() statuses: Lookups[] = [];
  @Input() sports: Sport[] = [];
  @Input() lengthRanges: LengthRange[] = [];

  @Input() statusLabelPrefix?: string;

  @Input() filterBy: FilterKey[] = [];
  @Input() handleQueryParamsChange = false;

  @Input() showFilterChips: boolean = true;


  @Output() onFilterChange = new EventEmitter<PublicSearchFilter>();
  @Output() onInitFilter = new EventEmitter<PublicSearchFilter>();

  readonly CalendarViewType = CalendarViewType;
  readonly environment = environment;

  chosenMonthRange?: { from?: NgbDate; to?: NgbDate };
  searchTerm?: string;
  showClearAll = false;

  filterGroups: Map<FilterKey, FilterGroup> = new Map<FilterKey, FilterGroup>();

  private validLengthValues = new Set<number>();
  private validCountryValues = new Set<number>();
  private validSportValues = new Set<number>();
  private validStatusValues = new Set<number>();

  private queryParamsFirstChange = true;


  constructor(private transloco: TranslocoService, private router: Router, private route: ActivatedRoute,
              private localStorageService: LocalStorageService) {
    super();
  }

  ngOnInit(): void {
    this.initValidators();
    this.initFilterGroups();
    if (this.handleQueryParamsChange) {
      this.applyQueryParams(this.route.snapshot.queryParams);
      this.onInitFilter.emit(this.getSearchFilter())
      this.subscribeToQueryParams();
    }
    const now = new Date();
    if (this.disablePastMonths) {
      this.minDate = new NgbDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
    }
    if (this.disableFutureMonths) {
      this.maxDate = new NgbDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
    }


  }

  private initValidators(): void {
    EventFilter.LENGTH_RANGES.forEach(r => {
      if (r.from != null) this.validLengthValues.add(r.from);
      if (r.to != null) this.validLengthValues.add(r.to);
    });
    this.validSportValues = new Set<number>(this.sports?.filter(s => s.id).map(s => s.id!));
    this.validCountryValues = new Set<number>(this.countries?.filter(c => c.id).map(c => c.id!));
    this.validStatusValues = new Set<number>(this.statuses?.filter(s => s.id).map(s => s.id!));
  }

  private initFilterGroups(): void {
    const all = new Map<FilterKey, FilterGroup>([
      [
        'sports', new FilterGroup(
          'sports',
          'sports',
          'app.common.sport',
          FilterGroupType.MULTI_SELECT,
          true,
         []
        )
      ],
      [
        'country',
          new FilterGroup(
        'country',
              'country',
              'app.common.country', FilterGroupType.MULTI_SELECT,
              false, []
        )
      ],
      [
        'length',
        new FilterGroup(
            'length', 'length',
            'explore.races.length',
            FilterGroupType.SINGLE_SELECT,false, []
  )
      ],
      [
        'status',
        new FilterGroup(
            'status', 'status', 'app.common.status', FilterGroupType.SINGLE_SELECT, false, []
        )
      ]
    ]);

    this.filterBy.forEach(key => {
      const group = all.get(key);
      if (group) {
        let items: FilterItem [];
        switch (key) {
          case "status":
            items = this.statusLabelPrefix ? this.getFilterItems(this.statuses, this.statusLabelPrefix) : []
            break;
          case "length":
            items = this.getLengthItems()
            break;
          case "country":
            items = this.getFilterItems(this.countries, 'app.common.country.')
            break;
          case "sports":
            items = this.getFilterItems(this.sports, 'app.common.sports.')
            break;
        }
        group.items = [...items];
        this.filterGroups.set(key, group)
      }
    });
  }

  getFilterItems(items: {id?: number}[], labelKey: string): FilterItem[] {
    return items
        .filter(i => i.id)
        .map(
            i =>
                new FilterItem(
                    i.id!,
                    this.t(`${labelKey}${i.id}`),
                    `${labelKey}${i.id}`
                )
        );
  }

  getLengthItems(): FilterItem[] {
    return this.lengthRanges.map((r, i) => {
      let label = '';
      if (r.from && r.to) label = `${r.from} - ${r.to} km`;
      else if (r.to)
        label = `${this.t('explore.races.length.to')} ${r.to} km`;
      else if (r.from)
        label = `${this.t('explore.races.length.over')} ${r.from} km`;
      return new FilterItem(i, label);
    })
  }


  private subscribeToQueryParams(): void {
    this.subscriptions.push(
        this.route.queryParams
            .pipe(debounceTime(environment.fullTextSearchDelay))
            .subscribe(params => {
              this.applyQueryParams(params);
              if (!this.queryParamsFirstChange) {
                this.onFilterChange.emit(this.getSearchFilter());
              }
              this.queryParamsFirstChange = false;
            })
    );
  }

  private applyQueryParams(params: Params): void {
    this.searchTerm = params[PublicSearchFilterQueryParams.NAME];

    if (this.showDateFilter) {
      const parseDate = (k: PublicSearchFilterQueryParams) =>
          this.getNgbDateFromString(params[k]);

      let from = parseDate(PublicSearchFilterQueryParams.DATE_FROM);
      let to = parseDate(PublicSearchFilterQueryParams.DATE_TO);
      if (this.minDate) {
        if (from?.before(this.minDate)) from = undefined;
        if (to?.before(this.minDate)) to = undefined;
      }
      if (this.maxDate) {
        if (from?.after(this.maxDate)) from = undefined;
        if (to?.after(this.maxDate)) to = undefined;
      }

      this.chosenMonthRange = { from, to };
    }


    this.applySelection('sports',
        this.getIdValue(params, PublicSearchFilterQueryParams.SPORTS, this.validSportValues)
    );
    this.applySelection( 'country',
        this.getIdValue(params, PublicSearchFilterQueryParams.COUNTRIES, this.validCountryValues)
    );
    this.applySelection( 'status',
        this.getIdValue(params, PublicSearchFilterQueryParams.STATUS, this.validStatusValues)
    );

    const lf = this.getLengthValue(params[PublicSearchFilterQueryParams.LENGTH_FROM]);
    const lt = this.getLengthValue(params[PublicSearchFilterQueryParams.LENGTH_TO]);

    this.filterGroups.get("length")?.items.forEach((item, idx) => {
      const r = this.lengthRanges[idx];
      item.selected = r?.from === lf && r?.to === lt;
    });

    this.showClearAll = this.isFilterActive();
  }


  isDateInRange(date: NgbDate | null): boolean {
    if (!date) return false;

    if (this.minDate && date.before(this.minDate)) return false;
    if (this.maxDate && date.after(this.maxDate)) return false;

    return true;
  }

  changeFilter(items: FilterItem[], key: FilterKey): void {
    const selected = new Set(items.filter(i => i.selected).map(i => i.id));
    this.filterGroups.get(key)?.items.forEach(
        i => (i.selected = selected.has(i.id))
    );
    this.filterChanged();
  }

  removeFilter(key: FilterKey, item: FilterItem): void {
    const target = this.filterGroups.get(key)?.items.find(i => i.id === item.id);
    if (target) target.selected = false;
    this.filterChanged();
  }

  clearAllFilters(): void {
    this.searchTerm = undefined;
    this.chosenMonthRange = undefined;
    Array.from(this.filterGroups.values()).flatMap(g => g!.items)
        .forEach(i => (i.selected = false));
    this.filterChanged();
  }

  dateChanged(value?: { from?: NgbDate; to?: NgbDate }): void {
    this.chosenMonthRange = value;
    this.filterChanged();
  }

  handleInput(value: string): void {
    this.searchTerm = value.trim();
    this.filterChanged();
  }


  changeLayout(layout: CalendarViewType): void {
    this.localStorageService.setLayout(layout);
  }

  get currentLayout(): CalendarViewType {
    return this.localStorageService.currentLayout;
  }

  selectSingleItem(item: FilterItem, key: FilterKey): void {
    const group = this.filterGroups.get(key);
    if (!group) return;
    group.items.forEach(i => i.selected = i.id === item.id);
    this.filterChanged();
  }

  clearGroupFilter(key: FilterKey): void {
    this.filterGroups.get(key)?.items.forEach(i => i.selected = false);
    this.filterChanged();
  }

  isGroupEmpty(key: FilterKey): boolean {
    return !this.filterGroups.get(key)?.items.some(i => i.selected);
  }

  private filterChanged(): void {
    this.showClearAll = this.isFilterActive();
    if (this.handleQueryParamsChange) {
      this.changeQueryParams();
    }
    else {
      this.onFilterChange.emit(this.getSearchFilter());
    }
  }

  private getSearchFilter(): PublicSearchFilter {
    return {
      name: this.searchTerm,
      dateFrom: this.showDateFilter ? getDateFromNgbDate(this.chosenMonthRange?.from) : undefined,
      dateTo: this.showDateFilter ? getDateFromNgbDate(this.chosenMonthRange?.to) : undefined,
      sports: this.filterGroups.get("sports")?.items
          .filter(i => i.selected)
          .map(i => i.id!),
      countries: this.filterGroups.get("country")?.items
          .filter(i => i.selected)
          .map(i => i.id!),
      status: this.filterGroups.get("status")?.items.find(i => i.selected)?.id,
      ...this.getSelectedRange()
    };
  }

  private getSelectedRange(): { lengthFrom?: number; lengthTo?: number } | {} {
    const index = this.filterGroups.get("length")?.items.find(i => i.selected)?.id;
    if (index == null) return {};
    const r = this.lengthRanges[index];
    return { lengthFrom: r.from, lengthTo: r.to };
  }

  private isFilterActive(): boolean {
    return (
        Array.from(this.filterGroups.values()).some(group =>
            group.items.some(item => item.selected)
        ) ||
        !!this.chosenMonthRange?.from ||
        !!this.chosenMonthRange?.to
    );
  }


  private applySelection(key: FilterKey, ids?: number[]): void {
    const set = new Set(ids ?? []);
    this.filterGroups.get(key)?.items.forEach(
        i => (i.selected = i.id != null && set.has(i.id))
    );
  }

  private getNgbDateFromString(value?: string): NgbDate | undefined {
    if (!value) return;
    const [y, m, d] = value.split('-').map(Number);
    return y && m && d ? new NgbDate(y, m, d) : undefined;
  }

  private getLengthValue(value?: string): number | undefined {
    const num = Number(value);
    return this.validLengthValues.has(num) ? num : undefined;
  }

  private getIdValue(params: Params, key: PublicSearchFilterQueryParams, validValues: Set<number>): number[] | undefined {
    const { ids } = parseIdListParam(params, key);
    return ids.every(id => validValues.has(id)) ? ids : undefined;
  }

  private changeQueryParams(): void {
    const f = this.getSearchFilter();
    const q: any = {};

    if (f.name) q.name = f.name;
    if (f.dateFrom) q.dateFrom = normalizedDate(f.dateFrom);
    if (f.dateTo) q.dateTo = normalizedDate(f.dateTo);
    if (f.sports?.length) q.sports = f.sports.join(',');
    if (f.countries?.length) q.countries = f.countries.join(',');
    if (f.lengthFrom) q.lengthFrom = f.lengthFrom;
    if (f.lengthTo) q.lengthTo = f.lengthTo;
    if (f.status) q.status = f.status;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: q,
      replaceUrl: true
    });
  }

  private t(key: string): string {
    return this.transloco.translate(key);
  }

}



